import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, Dialog, DialogContent, DialogTitle, Divider, IconButton, Snackbar, Switch, TextField, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';
import VibrationIcon from '@mui/icons-material/Vibration';
import SendIcon from '@mui/icons-material/Send';
import { useTranslation } from 'react-i18next';
import soundVibrationService from '../../services/SoundVibrationService';
import gameLogger from '../../services/GameLogger';
import { useAuth } from '../../context/AuthContext';
import { ApiError } from '../../services/online/apiClient';
import { getErrorMessageKey } from '../../services/online/errorMessages';
import { getAppVersion, getPlatform } from '../../utils/appInfo';

const MAX_SUGGESTION_LENGTH = 5000;

/**
 * Trimmed-down settings modal for an online match (TODO.md's "gestor de
 * sugerencias en base de datos" item) — no save/load game and no animation
 * speed, both meaningless once the server, not the client, owns match state.
 * Deliberately does NOT pause anything on open: the match is
 * server-orchestrated and keeps advancing for every seat regardless of
 * whether this dialog is open, unlike offline's GameSettingsInGameModal.
 */
export default function OnlineSettingsModal({ open, onClose, gameType, getGameSnapshot }) {
    const { t } = useTranslation();
    const { authFetch } = useAuth();
    const [soundEnabled, setSoundEnabled] = useState(true);
    const [vibrationEnabled, setVibrationEnabled] = useState(true);
    const [message, setMessage] = useState('');
    const [sending, setSending] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    useEffect(() => {
        if (!open) return;
        setSoundEnabled(soundVibrationService.isSoundEnabled());
        setVibrationEnabled(soundVibrationService.isVibrationEnabled());
    }, [open]);

    const handleSoundToggle = (event) => {
        const enabled = event.target.checked;
        setSoundEnabled(enabled);
        soundVibrationService.setSoundEnabled(enabled);
    };

    const handleVibrationToggle = (event) => {
        const enabled = event.target.checked;
        setVibrationEnabled(enabled);
        soundVibrationService.setVibrationEnabled(enabled);
    };

    const handleSendSuggestion = async () => {
        const trimmed = message.trim();
        if (!trimmed || sending) return;
        setSending(true);
        try {
            const [appVersion, platform] = [await getAppVersion(), getPlatform()];
            const gameSnapshot = {
                gameType,
                logs: gameLogger.getLogs(),
                ...(getGameSnapshot ? getGameSnapshot() : {}),
            };
            await authFetch('/suggestions', {
                method: 'POST',
                body: { message: trimmed, appVersion, platform, gameSnapshot },
            });
            setMessage('');
            setSnackbar({ open: true, message: t('online.settings.suggestion_sent'), severity: 'success' });
        } catch (err) {
            const key = err instanceof ApiError ? getErrorMessageKey(err.code) : 'online.errors.generic';
            setSnackbar({ open: true, message: t(key), severity: 'error' });
        } finally {
            setSending(false);
        }
    };

    return (
        <>
            <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
                <DialogTitle sx={{ pb: 1 }}>
                    {t('online.settings.title')}
                    <IconButton
                        aria-label="close"
                        onClick={onClose}
                        sx={{ position: 'absolute', right: 8, top: 8, color: (theme) => theme.palette.grey[500] }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers>
                    <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle1" gutterBottom>
                            {t('settings.sound_vibration')}
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    {soundEnabled ? <VolumeUpIcon color="primary" /> : <VolumeOffIcon color="disabled" />}
                                    <Typography variant="body1">{t('settings.sound')}</Typography>
                                </Box>
                                <Switch checked={soundEnabled} onChange={handleSoundToggle} color="primary" />
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <VibrationIcon color={vibrationEnabled ? 'primary' : 'disabled'} />
                                    <Typography variant="body1">{t('settings.vibration')}</Typography>
                                </Box>
                                <Switch checked={vibrationEnabled} onChange={handleVibrationToggle} color="primary" />
                            </Box>
                        </Box>
                    </Box>

                    <Divider sx={{ my: 2 }} />

                    <Box>
                        <Typography variant="subtitle1" gutterBottom>
                            {t('online.settings.suggestion_label')}
                        </Typography>
                        <TextField
                            multiline
                            rows={4}
                            placeholder={t('online.settings.suggestion_placeholder')}
                            value={message}
                            onChange={(e) => setMessage(e.target.value)}
                            fullWidth
                            inputProps={{ maxLength: MAX_SUGGESTION_LENGTH }}
                            helperText={`${message.length}/${MAX_SUGGESTION_LENGTH}`}
                            sx={{ mb: 1 }}
                        />
                        <Button
                            variant="outlined"
                            fullWidth
                            startIcon={<SendIcon />}
                            onClick={handleSendSuggestion}
                            disabled={sending || !message.trim()}
                        >
                            {sending ? t('online.settings.suggestion_sending') : t('online.settings.suggestion_send')}
                        </Button>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, textAlign: 'center' }}>
                            {t('online.settings.suggestion_help')}
                        </Typography>
                    </Box>

                    <Divider sx={{ my: 2 }} />

                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center' }}>
                        {t('settings.version')}: {process.env.REACT_APP_VERSION}
                    </Typography>
                </DialogContent>
            </Dialog>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={() => setSnackbar((s) => ({ ...s, open: false }))} severity={snackbar.severity} sx={{ width: '100%' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </>
    );
}
